import { BRANDING_ASSET_PATHS } from '@command-center/core-platform';

export const PORTAL_KEYS = ['oneup', 'cascade', 'mpf', 'ecorp', 'chicodd'] as const;

export type PortalKey = (typeof PORTAL_KEYS)[number];
export type PortalAuthAction = 'recovery' | 'invite';

export interface PortalRegistryEntry {
  key: PortalKey;
  appId: 'client-oneup' | 'admin-cascade' | 'admin-mpf' | 'admin-ecorp' | 'admin-chicodd';
  appName: string;
  productionUrl: string;
  notificationBaseUrl: string;
  /** Only set when this app exposes notification controls. */
  notificationSettingsPath?: string;
  postAuthUrl: string;
  cloudflare: { pagesProject: string; pagesUrl: string };
  authorization: {
    registeredApplicationId: string;
    scopeMode: 'fixed_client' | 'multi_client';
    clientId?: string;
    membershipRequired: boolean;
    enforcementStatus: 'verified' | 'unverified';
  };
  branding: {
    name: string;
    shortLabel: string;
    iconText: string;
    shortSuffix?: string;
    logoSrc: string;
    iconSrc: string;
    colors: { primary: string; background?: string; accent?: string };
  };
  auth: {
    recoveryPath: string;
    recoveryUrl: string;
    invitePath: string;
    inviteUrl: string;
  };
  /**
   * Routes that open one record, as `path?param=` prefixes an id is appended to.
   *
   * Notification emails build their "View X" links from here so a link can only
   * ever be the canonical production route of the portal that owns the record.
   * A portal with no surface for a record type simply omits the entry, and the
   * email is sent without a call to action rather than with one that 404s.
   */
  recordLinks?: {
    call?: string;
    formEntry?: string;
  };
}

const ACCOUNT_ORIGIN = 'https://app.oneuphi.com';

function entry(
  value: Omit<PortalRegistryEntry, 'notificationBaseUrl' | 'postAuthUrl' | 'auth' | 'branding'> & {
    branding: Omit<PortalRegistryEntry['branding'], 'logoSrc' | 'iconSrc'>;
  },
): PortalRegistryEntry {
  const suffix = value.key === 'oneup' ? '' : `/${value.key}`;
  const recoveryPath = `/auth/reset${suffix}`;
  const invitePath = `/auth/invite${suffix}`;
  return {
    ...value,
    branding: {
      ...value.branding,
      logoSrc: new URL(BRANDING_ASSET_PATHS.logo, value.productionUrl).toString(),
      iconSrc: new URL(BRANDING_ASSET_PATHS.icon, value.productionUrl).toString(),
    },
    notificationBaseUrl: value.productionUrl,
    postAuthUrl: value.productionUrl,
    auth: {
      recoveryPath,
      recoveryUrl: `${ACCOUNT_ORIGIN}${recoveryPath}`,
      invitePath,
      inviteUrl: `${ACCOUNT_ORIGIN}${invitePath}`,
    },
  };
}

/**
 * Production identity for every active Supabase-authenticated client portal.
 *
 * This private runtime registry is the allowlist for centralized account
 * routes and the source for portal URLs, notification bases, branding,
 * authorization scope, and Cloudflare Pages ownership. Do not accept a URL or
 * an unregistered portal key from a request in place of this registry.
 */
export const PORTAL_REGISTRY: Readonly<Record<PortalKey, PortalRegistryEntry>> = {
  oneup: entry({
    key: 'oneup',
    appId: 'client-oneup',
    appName: 'One Up Client Portal',
    productionUrl: 'https://app.oneuphi.com',
    notificationSettingsPath: '/settings?tab=notifications',
    cloudflare: { pagesProject: 'client-oneup', pagesUrl: 'https://client-oneup.pages.dev' },
    authorization: {
      registeredApplicationId: 'client-oneup',
      scopeMode: 'multi_client',
      membershipRequired: true,
      enforcementStatus: 'verified',
    },
    branding: {
      name: 'One Up Portal',
      shortLabel: 'One Up',
      shortSuffix: 'Portal',
      iconText: '1U',
      colors: { primary: '#79141a' },
    },
  }),
  cascade: entry({
    key: 'cascade',
    appId: 'admin-cascade',
    appName: 'Cascade',
    productionUrl: 'https://cascade.oneuphi.com',
    notificationSettingsPath: '/settings?tab=notifications',
    cloudflare: {
      pagesProject: 'admin-cascade-web',
      pagesUrl: 'https://admin-cascade-web.pages.dev',
    },
    authorization: {
      registeredApplicationId: 'admin-cascade',
      scopeMode: 'fixed_client',
      clientId: 'cascade',
      membershipRequired: true,
      enforcementStatus: 'verified',
    },
    branding: {
      name: 'Cascade Command Center',
      shortLabel: 'Cascade',
      iconText: 'CS',
      colors: { primary: '#1d4ed8', background: '#f7f7f8', accent: '#e8eefb' },
    },
    recordLinks: {
      call: '/ai-systems/voice-agents?call=',
      formEntry: '/forms-feed?entry=',
    },
  }),
  mpf: entry({
    key: 'mpf',
    appId: 'admin-mpf',
    appName: 'MPF',
    productionUrl: 'https://mpf.oneuphi.com',
    notificationSettingsPath: '/settings/notifications',
    cloudflare: { pagesProject: 'admin-mpf-web', pagesUrl: 'https://admin-mpf-web.pages.dev' },
    authorization: {
      registeredApplicationId: 'admin-mpf-web',
      scopeMode: 'fixed_client',
      clientId: 'mpf',
      membershipRequired: false,
      enforcementStatus: 'verified',
    },
    branding: {
      name: 'MPF Command Center',
      shortLabel: 'MPF',
      iconText: 'MPF',
      colors: { primary: '#1e40af', background: '#f7f7f8', accent: '#dbeafe' },
    },
  }),
  ecorp: entry({
    key: 'ecorp',
    appId: 'admin-ecorp',
    appName: 'ECORP',
    productionUrl: 'https://ecorp.oneuphi.com',
    cloudflare: {
      pagesProject: 'admin-ecorp-web',
      pagesUrl: 'https://admin-ecorp-web.pages.dev',
    },
    authorization: {
      registeredApplicationId: 'admin-ecorp',
      scopeMode: 'fixed_client',
      clientId: 'ecorp',
      membershipRequired: true,
      enforcementStatus: 'verified',
    },
    branding: {
      name: 'ECORP Project Companion',
      shortLabel: 'ECORP',
      iconText: 'EC',
      colors: { primary: '#6D4AFF', accent: '#8B72FF' },
    },
  }),
  chicodd: entry({
    key: 'chicodd',
    appId: 'admin-chicodd',
    appName: 'Chico Daily Deals',
    productionUrl: 'https://admin.chicodd.com',
    cloudflare: {
      pagesProject: 'admin-chicodd-web',
      pagesUrl: 'https://admin-chicodd-web.pages.dev',
    },
    authorization: {
      registeredApplicationId: 'admin-chicodd',
      scopeMode: 'fixed_client',
      clientId: 'chicodd',
      membershipRequired: true,
      enforcementStatus: 'verified',
    },
    branding: {
      name: 'Chico Daily Deals Admin',
      shortLabel: 'Chico Daily Deals',
      iconText: 'CDD',
      colors: { primary: '#C2410C', background: '#F8FAFC', accent: '#FFEDD5' },
    },
  }),
};

export function isPortalKey(value: string): value is PortalKey {
  return Object.prototype.hasOwnProperty.call(PORTAL_REGISTRY, value);
}

export function getPortal(value: PortalKey): PortalRegistryEntry {
  return PORTAL_REGISTRY[value];
}

/** Identity of the app owning the notification settings, independent of sender domain. */
export function portalNotificationSource(key: PortalKey) {
  const portal = getPortal(key);
  return {
    appName: portal.appName,
    settingsUrl: portal.notificationSettingsPath
      ? new URL(portal.notificationSettingsPath, portal.productionUrl).toString()
      : undefined,
    logoUrl: portal.branding.logoSrc,
  };
}

export function isPortalAuthReady(value: PortalKey): boolean {
  return getPortal(value).authorization.enforcementStatus === 'verified';
}

export function findPortalByAppId(appId: string): PortalRegistryEntry | undefined {
  return Object.values(PORTAL_REGISTRY).find((portal) => portal.appId === appId);
}

export function portalAuthUrl(key: PortalKey, action: PortalAuthAction): string {
  const portal = getPortal(key);
  return action === 'recovery' ? portal.auth.recoveryUrl : portal.auth.inviteUrl;
}

export function portalUrl(key: PortalKey, path = ''): string {
  const base = getPortal(key).productionUrl;
  if (!path) return base;
  return new URL(path.replace(/^\/+/, ''), `${base}/`).toString();
}
